/**
 * TempestSkillEffect — Lightning tree Arc path apotheosis active.
 *
 * Enters a 10-second timed mode. Every 800ms, a chain strike originates from
 * the player's current position and hits up to 4 enemies within 2.5 tiles.
 * 100% damage retention across all chain hops (proximity auto-hit model,
 * not a bounce-penalty scenario). The player moves freely throughout —
 * unlike Stormcaller, there is no movement lock and no onChannelStateChange dep.
 *
 * Pulses fire on a fixed interval timer tracked in update(). The 12th pulse
 * fires at t=9600ms; the mode expires at t=10000ms.
 *
 * Optional Static charge consumption at activation (bonus damage applied
 * via the chain dep's sourceSkillId, not split per pulse).
 *
 * Very long cooldown (4000ms). deactivate() is a no-op — Tempest runs
 * its full 10 seconds and cannot be cancelled.
 *
 * Upgrade keys: none currently wired via resolver. The resolver dep is
 * present for future upgrade extensions.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see lightning-spec.md § Tempest (`tempest`)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TempestChainConfig {
  readonly originX: number;
  readonly originY: number;
  readonly maxTargets: number;
  readonly maxRangeTiles: number;
  readonly damageRetention: number;
  readonly mode: 'single' | 'all';
  readonly sourceSkillId: string;
}

export interface TempestChainHit {
  readonly targetId: string;
  readonly order: number;
  /** Always 1.0 for Tempest (100% retention). */
  readonly damageMultiplier: number;
}

export interface TempestChainResult {
  readonly hits: readonly TempestChainHit[];
}

export interface TempestDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  /**
   * Chain targeting: finds up to maxTargets enemies within maxRangeTiles of
   * the origin, applies damage via CombatHitResolver (wired at game layer),
   * and returns hit details. sourceSkillId feeds the damage event so that
   * Voltage's CC bonus applies automatically at the resolver layer.
   */
  readonly chain: {
    findChainTargets(config: TempestChainConfig): TempestChainResult;
  };
  /**
   * Current player world-space position. Called once per pulse to set the
   * chain origin — Tempest follows the moving player.
   */
  readonly getPlayerPosition: () => { readonly x: number; readonly y: number };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
  /**
   * Optional charge consumption at activation. When present, consumes
   * 'static_charge' and applies bonus damage through the chain dep's
   * sourceSkillId routing.
   */
  readonly charge?: {
    consume(chargeId: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TempestConfig {
  /** Very long cooldown tier: 4000ms. */
  readonly cooldownMs: number;
  /** Timed mode duration in ms. */
  readonly baseDurationMs: number;
  /** Interval between auto-pulses in ms. */
  readonly basePulseIntervalMs: number;
  /**
   * Max chain targets per pulse. Spec default: 4 (pending design confirmation).
   * @see lightning-spec.md Discrepancy Log — CRITICAL flag on tempest.
   */
  readonly maxTargetsPerPulse: number;
  /**
   * Chain range in tiles (initial search radius from player position).
   * Per spec: 2.5 tiles.
   */
  readonly chainRangeTiles: number;
  /**
   * Damage retention per hop. Tempest uses 100% retention —
   * proximity auto-hit model, all targets in a single pulse take equal damage.
   */
  readonly damageRetention: number;
}

const DEFAULT_CONFIG: TempestConfig = {
  cooldownMs: 4000,
  baseDurationMs: 10000,
  basePulseIntervalMs: 800,
  maxTargetsPerPulse: 4,
  chainRangeTiles: 2.5,
  damageRetention: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TempestSkillEffect implements SkillEffect {
  readonly skillId = 'tempest';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: TempestDeps;
  private readonly config: TempestConfig;

  private _cooldownRemaining = 0;

  // Mode state — reset on each activation.
  private _isActive = false;
  private _modeElapsed = 0;
  private _pulsesFired = 0;
  private _lastChargeConsumed = 0;

  // Per-mode tracking (reset on activate).
  /** Elapsed time at each pulse fire — used in scenario interval assertions. */
  private _pulseTimesMs: number[] = [];
  private _lastPulseResults: TempestChainResult[] = [];

  // Lifetime stats.
  private _activations = 0;
  private _fizzles = 0;
  private _totalPulsesFired = 0;
  private _totalTargetsHit = 0;
  private _modesCompleted = 0;

  constructor(deps: TempestDeps, config: Partial<TempestConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for scenario assertions) ---

  get cooldown(): number { return this._cooldownRemaining; }
  /** True while the 10-second Tempest mode is running. */
  get isTempestActive(): boolean { return this._isActive; }
  get modeElapsed(): number { return this._modeElapsed; }
  get pulsesFired(): number { return this._pulsesFired; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get totalPulsesFired(): number { return this._totalPulsesFired; }
  get totalTargetsHit(): number { return this._totalTargetsHit; }
  get modesCompleted(): number { return this._modesCompleted; }
  get lastChargeConsumed(): number { return this._lastChargeConsumed; }
  get pulseTimesMs(): readonly number[] { return this._pulseTimesMs; }
  get lastPulseResults(): readonly TempestChainResult[] { return this._lastPulseResults; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    if (!this.deps.rollSkillSuccess('tempest')) {
      this._fizzles++;
      return false;
    }

    this.deps.breakHide?.();

    // Optional charge consumption at activation.
    this._lastChargeConsumed = this.deps.charge?.consume('static_charge') ?? 0;

    // Enter timed mode.
    this._isActive = true;
    this._modeElapsed = 0;
    this._pulsesFired = 0;
    this._pulseTimesMs = [];
    this._lastPulseResults = [];

    this._activations++;
    return true;
  }

  /**
   * Tempest cannot be cancelled — the storm runs its full 10 seconds.
   * Intentional no-op.
   */
  deactivate(): void {
    // Intentionally empty.
  }

  isActive(): boolean {
    return this._isActive;
  }

  update(delta: number): void {
    // Tick cooldown.
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick mode.
    if (this._isActive) {
      this._modeElapsed += delta;

      // Fire any pulses now due. Pulses fire at scheduled indices:
      // pulse 1 at basePulseIntervalMs, pulse 2 at 2×, …
      // Math.floor(duration / interval) total pulses over the mode.
      while (true) {
        const nextPulseAt = (this._pulsesFired + 1) * this.config.basePulseIntervalMs;
        if (this._modeElapsed >= nextPulseAt) {
          this._firePulse();
        } else {
          break;
        }
      }

      // End mode when full duration has elapsed.
      if (this._modeElapsed >= this.config.baseDurationMs) {
        this._endMode();
      }
    }
  }

  private _firePulse(): void {
    const pos = this.deps.getPlayerPosition();
    const chainConfig: TempestChainConfig = {
      originX: pos.x,
      originY: pos.y,
      maxTargets: this.config.maxTargetsPerPulse,
      maxRangeTiles: this.config.chainRangeTiles,
      damageRetention: this.config.damageRetention,
      mode: 'single',
      sourceSkillId: 'tempest',
    };

    const result = this.deps.chain.findChainTargets(chainConfig);

    this._pulseTimesMs.push(this._modeElapsed);
    this._lastPulseResults.push(result);
    this._totalTargetsHit += result.hits.length;
    this._pulsesFired++;
    this._totalPulsesFired++;
  }

  private _endMode(): void {
    this._isActive = false;
    // Cooldown begins when the mode ends, not at activation.
    this._cooldownRemaining = this.config.cooldownMs;
    this._modesCompleted++;
  }

  destroy(): void {
    if (this._isActive) {
      this._endMode();
    }
    this._cooldownRemaining = 0;
  }
}
